import React, { useCallback, useRef } from "react";
import { List } from "../../Components/common/List/List";
import { ListProvider } from "../../Components/common/List/Providers/List";
import { useAppContext } from "../../AppProvider";
import { createModalConfig, ModalConfig } from "../../Models/ModalConfig";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare, faTrash } from "../../utilities/fontawesomeIcons";
import { Modal, ModalBody } from "reactstrap";
import { useIndexParcelTemplatesContext } from "./providers/Index";
import { useListProps } from "./utilities/list";
import { ParcelTemplate } from "../../Models/ParcelTemplate";
import { DeleteParcelTemplateProvider } from "./providers/Delete";
import Delete from "./Delete.tsx";
import { HandlerParcelTemplateProvider } from "./providers/Handler.tsx";
import Handler from "./Handler.tsx";
import { useTypes } from "../../utilities/types.js";

const AnyListProvider = ListProvider as any;
const AnyList = List as any;

export const ParcelTemplates: React.FC = () => {
  const listRef = useRef<{ refresh: () => void } | null>(null);
  const listProps = useListProps();
  const indexParcelTemplatesContext = useIndexParcelTemplatesContext();
  const modalConfig = indexParcelTemplatesContext?.modalConfig;
  const setModalConfig = indexParcelTemplatesContext?.setModalConfig;

  const renderItem = useCallback((item: ParcelTemplate, index: number) => {
    return <ListItem listRef={listRef} item={item} index={index} />;
  }, []);

  const closeModal = () => {
    if (setModalConfig) {
      setModalConfig(createModalConfig());
    }
  };

  return (
    <>
      <AnyListProvider path={listProps.path} sorting={listProps.sorting}>
        <AnyList
          ref={listRef}
          title={listProps.title}
          create={listProps.create}
          banner={listProps.banner}
          massiveActions={listProps.massiveActions}
          renderItem={renderItem}
          pagination={listProps.pagination}
          selectRowsEnabled={listProps.selectRowsEnabled}
          type={listProps.type}
        />
      </AnyListProvider>

      <Modal isOpen={modalConfig?.isOpen} size={modalConfig?.size}>
        <ModalBody className={modalConfig?.className || ""}>
          {modalConfig?.action === "create" && (
            <HandlerParcelTemplateProvider
              autosave={true}
              callback={() => {
                closeModal();
                listRef.current?.refresh();
              }}
            >
              <Handler />
            </HandlerParcelTemplateProvider>
          )}
          {modalConfig?.action == "edit" && (
            <HandlerParcelTemplateProvider
              autosave={true}
              id={modalConfig?.data?.[0]}
              callback={() => {
                closeModal();
                listRef.current?.refresh();
              }}
            >
              <Handler />
            </HandlerParcelTemplateProvider>
          )}
          {modalConfig.action === "delete" && (
            <DeleteParcelTemplateProvider
              data={modalConfig.data}
              callback={() => {
                listRef.current?.refresh();
                closeModal();
              }}
            >
              <Delete />
            </DeleteParcelTemplateProvider>
          )}
        </ModalBody>
      </Modal>
    </>
  );
};

export default ParcelTemplates;

const ListItem: React.FC<{
  listRef: React.RefObject<{
    refresh: () => void;
  } | null>;
  item: ParcelTemplate;
  index: number;
}> = React.memo(({ item }) => {
  const { dictionary } = useAppContext();
  const indexParcelTemplatesContext = useIndexParcelTemplatesContext();
  const { parcels } = useTypes();

  const openModal = useCallback(
    (
      action: ModalConfig["action"],
      data?: string[],
      size: ModalConfig["size"] = "sm",
      className = "",
      title = ""
    ) => {
      indexParcelTemplatesContext?.setModalConfig({
        isOpen: true,
        action,
        size,
        data,
        className,
        title,
      });
    },
    [indexParcelTemplatesContext]
  );

  const icons = {
    parcelType: parcels.find((o) => o.value === item.parcelTypeCode),
  };

  const gridTemplateColumns = "1fr 1fr";

  const gridTemplateAreas = `
      "icon icon"
      "name name"
      "dimensions dimensions"
      "weight volume"
      "row row"
      "edit delete"
    `;

  return (
    <>
      <div className={`p-3`}>
        <div
          className="d-grid"
          style={{
            gridTemplateColumns: gridTemplateColumns,
            gridTemplateAreas: gridTemplateAreas,
            gap: "0.5rem 1rem",
          }}
        >
          <div
            className="d-flex justify-content-center "
            style={{ gridArea: "icon" }}
          >
            <div
              className="d-flex justify-content-center align-items-center"
              style={{
                width: "50px",
                height: "50px",
                borderRadius: "50%",
                backgroundColor: "#f5f5f5",
              }}
            >
              <FontAwesomeIcon
                icon={icons?.parcelType?.icon}
                title={icons.parcelType?.label}
              />
            </div>
          </div>

          <div style={{ gridArea: "name" }}>
            <label className="text-center label-4 mb-2">
              {item.name || "-"}
            </label>
          </div>

          <div style={{ gridArea: "dimensions" }}>
            <label className="label-2">
              {dictionary.dimensions.width_abbreviation +
                " x " +
                dictionary.dimensions.height_abbreviation +
                " x " +
                dictionary.dimensions.length_abbreviation +
                " (cm)"}
            </label>
            <div>
              <label className="label-3">
                {`${item.width?.toFixed(2) || " - "} x ${
                  item.height?.toFixed(2) || " - "
                } x ${item.length?.toFixed(2) || " - "}`}
              </label>
            </div>
          </div>

          <div style={{ gridArea: "weight" }}>
            <label className="label-2">
              {dictionary.dimensions.weight + " (Kg)"}
            </label>
            <div>
              <label className="label-3">
                {item.weight?.toFixed(2) || "-"}
              </label>
            </div>
          </div>

          <div style={{ gridArea: "volume" }}>
            <label className="label-2">
              {dictionary.dimensions.volume + " (m3)"}
            </label>
            <div>
              <label className="label-3">
                {item.volume?.toFixed(6) || "-"}
              </label>
            </div>
          </div>

          <hr
            className="my-2 "
            style={{ gridArea: "row", border: "1px solid #ccc" }}
          />

          <button
            style={{ gridArea: "edit" }}
            title={dictionary.actions.edit}
            className="button-1 button-1-light"
            onClick={() => {
              openModal("edit", [item.id], "md", "", dictionary.actions.edit);
            }}
          >
            <FontAwesomeIcon icon={faPenToSquare} />
            <span>Modifica</span>
          </button>

          <button
            style={{ gridArea: "delete" }}
            title={dictionary.actions.delete}
            className="button-1 button-1-danger"
            onClick={() => {
              openModal(
                "delete",
                [item.id],
                "sm",
                "",
                dictionary.actions.delete
              );
            }}
          >
            <FontAwesomeIcon icon={faTrash} />
            <span>Elimina</span>
          </button>
        </div>
      </div>
    </>
  );
});
